import React, { useEffect, useState } from 'react';
import PageHeading from '../../components/PageHeading';
import { FileImageOutlined, UploadOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Form, Input, Row, Col, Select, Space, Divider, 
	Upload, Button, Typography, Checkbox, message } from 'antd';
import axiosInstance from '../../utils/axios';
import { useNavigate, useParams } from 'react-router-dom';

const { Option } = Select;

const AddMovie = () => {
	const { id } = useParams();
	const isUpdate = Boolean(id);
	const navigate = useNavigate();
	
  	const [form] = Form.useForm();
	const [genre, setGenre] = useState([]);
	const [language, setLanguage] = useState([]);
	const [thumbnail, setThumbnail] = useState(null);
	const [banner, setBanner] = useState(null);
	const [trailer, setTrailer] = useState(null);
	const [loading, setLoading] = useState(false);

	const [theatres, setTheatres] = useState([]);
	const [bookingLinks, setBookingLinks] = useState([]);


	const [confirmChange, setConfrimChange] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);

	useEffect(() => {
		fetchGenre();
		fetchLanguage();
		fetchTheatres();
	}, []);

	useEffect(() => {
		setBookingLinks([]);
		if (isUpdate) { 
			fetchDetail();
		} else {
			form.resetFields();
			setThumbnail(null);
			setTrailer(null);
			setBanner(null);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const fetchDetail = async () => {
		try {
			const { data } = await axiosInstance.get(`movie/movie/${id}/`);
			form.setFieldsValue({
				name: data.name,
				directed_by: data.directed_by,
				about: data.about,
				age: data.age,
				duration: data.duration,
				genre: data.genre,        
				language: data.language,
				starring: data.starring,
				music_by: data.music_by,
				status: data.status,
				// trailer_link: data.trailer_link
			});


			if (data.third_party_booking_links?.length > 0) {
				setBookingLinks(
					data.third_party_booking_links.map((item) => ({
					theatre: item.third_party_theater,
					link: item.link,
					}))
				);
			}

			if (data.thumbnail) {
				setThumbnail({
					uid: '-1',
					name: 'thumbnail.jpg',
					status: 'done',
					url: data.thumbnail,
				});
			}

			if (data.banner) {
				setBanner({
					uid: '-2',
					name: 'banner.jpg',
					status: 'done',
					url: data.banner,
				});
			}

			if (data.trailer) {
				setTrailer({
					uid: '-3',
					name: 'video.mp4',
					status: 'done',
					url: data.trailer,
				});
			}
		} catch {
			message.error('Failed to load detail');
		}
  	};

	const fetchGenre = async () => {
		try {
			const response = await axiosInstance.get("core/genre/");
			setGenre(response.data);
		} catch (error) {
			message.error("Failed to fetch genre");
		}
	}

	const fetchLanguage = async () => {
		try {
			const response = await axiosInstance.get("core/language/");
			setLanguage(response.data);
		} catch (error) {
			message.error("Failed to fetch languages");
		}
  	}

	const fetchTheatres = async () => {
		try {
			const response = await axiosInstance.get("core/external-theatre/");
			setTheatres(response.data);
		} catch (error) {
			message.error("Failed to fetch external Theatres");
		} finally {
			setLoading(false);
		}
	};


	const validateImage = (file) => {
		const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
		const isLt2M = file.size / 1024 / 1024 < 2;
		if (!isImage) {
			message.error('Only JPG/PNG files are allowed!');
			return Upload.LIST_IGNORE;
		}
		if (!isLt2M) {
			message.error('Image must be smaller than 2MB!');
			return Upload.LIST_IGNORE;
		}
		return true;
	}

	const validateVideo = (file) => {
		const isMp4 = file.type === 'video/mp4';
		const isLt10M = file.size / 1024 / 1024 < 10;
		if (!isMp4) {
			message.error('Only MP4 video is allowed!');
			return Upload.LIST_IGNORE;
		}
		if (!isLt10M) {
			message.error('Video must be smaller than 10MB!');
			return Upload.LIST_IGNORE;
		}
		return true;
	}

	const getOriginFile = (file) => {
    	return file?.originFileObj || null;
 	}

	const handleAddLink = () => {
		setBookingLinks([...bookingLinks, { theatre: null, link: '' }]);
	};

	const handleRemoveLink = (index) => {
		const updated = [...bookingLinks];
		updated.splice(index, 1);
		setBookingLinks(updated);
	};

	const handleChangeLink = (index, field, value) => {
		const updated = [...bookingLinks];
		updated[index][field] = value;
		setBookingLinks(updated);
	};

	const handleFinish = async (values) => {
		setLoading(true);
		const fd = new FormData();

		// Append text fields
		fd.append('name', values.name);
		fd.append('about', values.about);
		fd.append('starring', values.starring || '');
		fd.append('music_by', values.music_by || '');
		fd.append('directed_by', values.directed_by || '');
		fd.append('duration', values.duration || '');
		fd.append('age', values.age);
		fd.append('language', values.language || '');
		fd.append('status', values.status || 'coming_soon'); 
		// fd.append('trailer_link', values.trailer_link || '');

		// genre: array of ids
		if (values.genre && values.genre.length > 0) {
			values.genre.forEach(id => {
				fd.append('genre', id);
			});
		}

		// Append files/7/7
		const thumbFile = getOriginFile(thumbnail);
		const bannerFile = getOriginFile(banner);
		const trailerFile = getOriginFile(trailer);

		if (thumbFile) fd.append('thumbnail', thumbFile);
		if (bannerFile) fd.append('banner', bannerFile);
		if (trailerFile) fd.append('trailer', trailerFile);

		if (bookingLinks.length > 0) {
      		fd.append('third_party_booking_links_data', JSON.stringify(bookingLinks));
    	}

		try {
			if (isUpdate) {
				await axiosInstance.patch(`movie/movie/${id}/`, fd);
				message.success('Movie updated successfully');
				navigate('/admin/movie/upcoming')
			} else {
				await axiosInstance.post('movie/movie/', fd);
				message.success('Movie created successfully');
				form.resetFields();
				setThumbnail(null);
				setBanner(null);
				setTrailer(null);
				setBookingLinks([]);
			}
		} catch (err) {
			console.error(err);
			message.error('Failed to create movie');
		} finally {
			setLoading(false);
		}
	};

	const changeStatus = async () => {
		setLoading(true);
    try {
      await axiosInstance.patch(`movie/movie/${id}/`, {status: 'archived'});
      navigate('/admin/movie/upcoming');
    } catch (error) {
      message.error("Cannot change status");
    } finally {
			setLoading(false);
		}
  }

	const deleteMovie = async () => {
    try {
      await axiosInstance.delete(`movie/movie/${id}/`);
      navigate('/admin/movie/upcoming');
    } catch (error) {
      message.error("Cannot delete");
    } finally {
			setLoading(false);
		}
  }


  return (
	<div className='dashboard'>
		<PageHeading name="Add New Movie" icon={<FileImageOutlined />} />
			<Row gutter={[16, 16]}>
				<Col xs={24} sm={24} md={24} lg={12}>
					<Form layout="vertical" form={form} name="form" onFinish={handleFinish}>
						<Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Movie Name"
									name="name"
									rules={[{ required: true, message: 'Please enter name' }]}
								>
									<Input />
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Directed By"
									name="directed_by"
								>
									<Input />
								</Form.Item>
							</Col>
						</Row>

						<Form.Item
							label="About"
							name="about"
							rules={[{ required: true, message: 'Please enter description' }]}
						>
							<Input.TextArea rows={6} />
						</Form.Item>

						<Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Age Restriction"
									name="age"
								>
									<Input type='number' />
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Time Duration"
									name="duration"
								>
									<Input placeholder='Eg: 2h 30min or 02:30:00' />
								</Form.Item>
							</Col>
						</Row>

						<Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Genre"
									name="genre"
								>
									<Select
										mode="multiple"
										allowClear
										style={{ width: '100%' }}
										placeholder="Please select"
										
									>
										{
											genre.map((item, index) => <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>)
										}
									</Select>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Language"
									name="language"
								>
									<Select>
										{
											language.map((item, index) => <Select.Option key={item.id} value={item.id}>{item.name} - {item.alias}</Select.Option>)
										}
									</Select>
								</Form.Item>
							</Col>
						</Row>

						<Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Starring"
									name="starring"
								>
									<Input.TextArea rows={4} placeholder='Eg: Surya, Vijay, Vikram (Comma Separated)'/>
								</Form.Item>
							</Col>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Music By"
									name="music_by"
								>
									<Input.TextArea rows={4} placeholder='Eg: Surya, Vijay, Vikram (Comma Separated)'/>
								</Form.Item>
							</Col>
						</Row>
						{isUpdate && <Row  gutter={[16, 16]}>
							<Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Status"
									name="status"
								>
									<Select>
										<Select.Option value="coming_soon">Upcoming</Select.Option>
										<Select.Option value="now_showing">Now Playing</Select.Option>
										<Select.Option disabled value="archived">Archived</Select.Option>
									</Select>
								</Form.Item>
							</Col>
							{/* <Col xs={24} sm={24} md={24} lg={12}>
								<Form.Item
									label="Trailer Link"
									name="trailer_link"
								>
									<Input />
								</Form.Item>
							</Col> */}
						</Row>}

						<Row gutter={[16, 16]}>
							<Col xs={24} sm={24} md={12} lg={8}>
								<Form.Item
									label="Thumbnail (Portrait)"
									required
								>
									<Upload 
										maxCount={1}
										fileList={thumbnail ? [thumbnail] : []}
										beforeUpload={(file) => {
											const valid = validateImage(file);
											if (valid !== true) return valid;
											setThumbnail({
												uid: file.uid,
												name: file.name,
												status: 'done',
												originFileObj: file,
											});
											return false;
										}}
										onRemove={() => setThumbnail(null)}
									>
										<Button icon={<UploadOutlined />}>Upload</Button>
									</Upload>
								</Form.Item>
							</Col>

							<Col xs={24} sm={24} md={12} lg={8}>
								<Form.Item label="Banner (Landscape)" required>
								<Upload
									maxCount={1}
									fileList={banner ? [banner] : []}
                  beforeUpload={(file) => {
                    const valid = validateImage(file);
                    if (valid !== true) return valid;
                    setBanner({
                      uid: file.uid,
                      name: file.name,
                      status: 'done',
                      originFileObj: file,
                    });
                    return false;
                  }}
                  onRemove={() => setBanner(null)}
								>
									<Button icon={<UploadOutlined />}>Upload</Button>
								</Upload>
								</Form.Item>
							</Col>

							<Col xs={24} sm={24} md={12} lg={8}>
								<Form.Item label="Trailer"> 
								<Upload 
									maxCount={1}
									fileList={trailer ? [trailer] : []}
                  beforeUpload={(file) => {
                    const valid = validateVideo(file);
                    if (valid !== true) return valid;
                    setTrailer({
                      uid: file.uid,
                      name: file.name,
                      status: 'done',
                      originFileObj: file,
                    });
                    return false;
                  }}
                  onRemove={() => setTrailer(null)}
								>
									<Button icon={<UploadOutlined />}>Upload</Button>
								</Upload>
								</Form.Item>
							</Col>
						</Row>
						<Form.Item>
							<Button type="primary" htmlType="submit" loading={loading}>
								Submit
							</Button>
						</Form.Item>
					</Form>
				</Col>

				<Col xs={24} sm={24} md={24} lg={12}>
				  	<div className="border-box box-default">
						<Typography.Title level={5} style={{marginTop: "0px"}}>Third Party Booking Links</Typography.Title>
						{bookingLinks.map((item, index) => (
							<div
								key={index}
								style={{
									border: '1px solid #eee',
									borderRadius: 8,
									padding: 12,
									marginBottom: 10
								}}
							>
								<Space direction="vertical" style={{ width: '100%' }}>
								<Select
									placeholder="Select Third Party Theatre"
									value={item.theatre}
									onChange={(value) => handleChangeLink(index, 'theatre', value)}
									style={{ width: '100%' }}
								>
									{theatres.map((th) => (
									<Option key={th.id} value={th.id}>
										{th.name}
									</Option>
									))}
								</Select>

								<Input
									placeholder="Booking link"
									value={item.link}
									onChange={(e) => handleChangeLink(index, 'link', e.target.value)}
								/>

								<Button
									type="text"
									danger
									icon={<DeleteOutlined />}
									onClick={() => handleRemoveLink(index)}
								>
									Remove
								</Button>
								</Space>
							</div>
						))}

						<Divider />
						<Button type="dashed" onClick={handleAddLink} icon={<PlusOutlined />}>
							Add Another Booking Link
						</Button>
					</div>
					{isUpdate ? <div className='border-box box-warning'>
						<Typography.Title level={5} color='error' style={{marginTop: "0px"}}>Warning</Typography.Title>
						<Typography.Paragraph>
							After changing status to Archive, then this movie will not available to assigning show and booking 
						</Typography.Paragraph>
						<Row>
							<Col>
								<Checkbox onChange={(e) => setConfrimChange(!confirmChange)}>Confirm to change status</Checkbox>
							</Col>
							<Col>
								<Button color="danger" variant="solid" onClick={() => changeStatus()} disabled={!confirmChange}>
									Archive
								</Button>
							</Col>
						</Row>
						
					</div> : null}
					{isUpdate ? <div className='border-box box-danger'>
						<Typography.Title level={5} color='error' style={{marginTop: "0px"}}>Danger zone</Typography.Title>
						<Typography.Paragraph>
							Do not delete if this movie is assigned to show or booking is undergoing
						</Typography.Paragraph>
						<Row>
							<Col>
								<Checkbox onChange={(e) => setConfirmDelete(!confirmDelete)}>Confirm to Delete</Checkbox>
							</Col>
							<Col>
								<Button color="danger" variant="solid" onClick={() => deleteMovie()} disabled={!confirmDelete}>
									Delete
								</Button>
							</Col>
						</Row>
						
					</div> : null}
				</Col>
			</Row>
		</div>
  )
}

export default AddMovie